<template>
  <div :class="statisticsClasses">
    <div
      class="statistics__message"
      v-if="dataAvailable"
    >
      <template v-if="!isChartToday">
        <p>{{ t('history.noPastDayPrice') }}</p>
      </template>
      <template v-if="isChartToday && hasConsumption && showPrice">
        <p>
          {{ t('history.avgConsumptionPricePre') }} <strong>{{ priceConsumption }}</strong> {{ t('history.perKwhSuffix') }}
        </p>
      </template>

      <template v-if="isChartToday && hasProduction && showPrice">
        <p>
          <template v-if="hasGenerationPlanStandardPrice || flexPlan">
            <span v-html="t('history.avgProductionPricePreIfAvailable', {'@priceProduction': priceProduction,})" />

            <p>
              <template v-if="hasGenerationPlanStandardPrice && fixFloatPlan">
                <span v-html="t('history.avgProductionPriceLegalHint')" />
              </template>
            </p>
          </template>
          <template v-else  v-html="">
            <span v-html="t('history.avgProductionPricePreIfNotAvailable', {'@tariffName': tariffName,})" />
          </template>
        </p>
      </template>

      <template v-if="isToday && isChartToday">
        <p>{{ t('history.tradingDataPre') }}
          <strong>{{ t('history.tradingDataToday') }} {{ dataTime }} {{ t('history.tradingDataPost') }}</strong>
        </p>
      </template>
      <template v-if="isMeterless">
        <p>{{ t('history.meterlessNote') }}</p>
      </template>
    </div>

    <template v-if="hasProduction">
      <h1 class="statistics__overview-title">{{ t('history.myEnergyMix') }}</h1>
      <div class="statistics__overview-consumption" data-cy="consumption">
        <PieChart
          v-if="dataAvailable"
          :headline="t('history.consumption')"
          :minEnergyAmount="minEnergyAmount"
          data-source="consumedEnergyTypes"
        >
          <template v-slot:content>
            <div class="pie-chart__content">
              {{ wordingFormattedDate }} {{ t('history.consumed') }}
              <strong>{{ formatKWH(totalConsumed) }}</strong>
            </div>
          </template>
        </PieChart>
      </div>
      <div class="statistics__overview-production" data-cy="production">
        <PieChart
          v-if="dataAvailable"
          :headline="t('history.production')"
          :minEnergyAmount="minEnergyAmount"
          data-source="producedEnergy"
        >
          <template v-slot:content>
            <div class="pie-chart__content">
              {{ wordingFormattedDate }} {{ t('history.produced') }}
              <strong>{{ formatKWH(totalProduced) }}</strong>
            </div>
          </template>
        </PieChart>
      </div>
    </template>
    <div v-else class="statistics__overview">
      <PieChart
        v-if="dataAvailable"
        :headline="t('history.myEnergyMix')"
        :minEnergyAmount="minEnergyAmount"
        data-source="consumedEnergyTypes"
      >
        <template v-slot:content>
          <div class="pie-chart__content">
            {{ wordingFormattedDate }} {{ t('history.consumed') }}
            <strong>{{ formatKWH(totalConsumed) }}</strong>
          </div>
        </template>
      </PieChart>
    </div>

    <div class="statistics__producers" data-cy="producers">
      <PieChart
        v-if="dataAvailable"
        :headline="t('history.myProducers')"
        :minEnergyAmount="minEnergyAmount"
        data-source="producers"
      >
        <template v-slot:content>
          <div class="pie-chart__content">
            {{ wordingFormattedDate }} {{ t('history.consumed') }}
            <strong>{{ formatKWH(producersTotal) }}</strong>
          </div>
        </template>
      </PieChart>

      <TradingStatistics
        v-if="dataAvailable"
        data-source="producers"
        :is-producers="true"
      />
    </div>

    <div class="statistics__consumers" v-if="hasConsumers">
      <PieChart
        v-if="dataAvailable"
        :headline="t('history.myConsumers')"
        :minEnergyAmount="minEnergyAmount"
        data-source="consumers"
      >
        <template v-slot:content>
          <div class="pie-chart__content">
            {{ wordingFormattedDate }} {{ t('history.delivered') }}
            <strong>{{ formatKWH(consumersTotal) }}</strong>
          </div>
        </template>
      </PieChart>

      <TradingStatistics
        v-if="dataAvailable"
        data-source="consumers"
        :is-producers="false"
        energy-type="pv"
      />
    </div>

    <div class="statistics__message--unobtrusive" v-if="isCommunityTradingHintVisible">
      {{ t('history.minEnergyNote', { '@amount': minEnergyAmountFormatted }) }}
    </div>
    <div class="statistics__message--unobtrusive" v-if="(flexPlan || fixPlan) && isCommunityTradingHintVisible">
      {{ t('history.priceDisclaimer') }}
    </div>
  </div>
</template>

<script lang="ts">
import PieChart from './PieChart.vue'
import TradingStatistics from './TradingStatistics.vue'
import { useUnits } from '@/composables/useUnits.ts'
import { useMyCube } from '@/composables/useMyCube.js'
import { useHistoryStore } from '@/stores/useHistoryStore'
import { useCubeStore } from '@/stores/useCubeStore'
import { useDate } from '@/composables/useDate.ts'
import { useStrings } from '@/composables/useStrings.ts'
import { MIN_ENERGY_TRACE } from '@/constants';
import { NumberFormatUtil } from '@/util/NumberFormatUtil';
import { usePlans } from '@/composables/usePlans';
import { useAccountStore } from '@/stores/useAccountStore'
const { t } = useStrings()

export default {
  name: 'power-statistics',
  components: {
    PieChart,
    TradingStatistics,
  },
  props: {
    timespan: {
      type: String,
      required: true,
    },
  },
  setup() {
    const historyStore = useHistoryStore()
    const cubeStore = useCubeStore()
    const dateHelpers = useDate()
    const plans = usePlans()
    const accountStore = useAccountStore()

    const { formatKWH } = useUnits()
    const { hasProduction, hasConsumption } = useMyCube()

    return {
      historyStore,
      cubeStore,
      formatKWH,
      hasProduction,
      hasConsumption,
      dateHelpers,
      plans,
      accountStore,
      t,
      minEnergyAmount: MIN_ENERGY_TRACE
    }
  },
  computed: {
    statisticsClasses() {
      let classes = ['statistics']

      if (!this.hasProduction) {
        classes.push('statistics--no-production')
      }
      return classes.join(' ')
    },
    dataAvailable() {
      return this.historyStore.tradingStatisticsReceived
    },
    hasGenerationPlanStandardPrice() {
      return this.plans.hasGenerationPlanStandardPrice();
    },
    isCommunityTradingHintVisible() {
      return !this.accountStore.properties.isCommunityless;
    },
    wordingFormattedDate() {
      if (this.timespan === 'day') {
        return this.t('history.dayPrefix') + ' ' + this.dateHelpers.formatWithDateFns(this.historyStore.historySelectedDate, 'DATE')
      }
      return this.t('history.monthPrefix') + ' ' + this.dateHelpers.formatWithDateFns(this.historyStore.historySelectedDate, 'MONTH')
    },
    producersTotal() {
      return this.historyStore.tradingStatisticsAggregate(
        'consumedFromCommunity'
      )
    },
    consumersTotal() {
      return this.historyStore.tradingStatisticsAggregate(
        'producedForCommunity'
      )
    },
    totalConsumed() {
      const community = this.historyStore.tradingStatisticsAggregate(
        'consumedFromCommunity'
      )
      const retailer = this.historyStore.tradingStatisticsAggregate(
        'consumedFromRetailer'
      )
      return community + retailer
    },
    totalProduced() {
      const community = this.historyStore.tradingStatisticsAggregate(
        'producedForCommunity'
      )
      const retailer = this.historyStore.tradingStatisticsAggregate(
        'producedForRetailer'
      )
      return community + retailer
    },
    hasConsumers() {
      return (
        this.historyStore.getTradingStatistics('consumers').data.length > 0
      )
    },
    isToday() {
      return this.dateHelpers.moment(this.historyStore.tradingStatisticsLastUpdate as Date).isSame(
        new Date(),
        'day'
      )
    },
    isChartToday() {
      return this.dateHelpers.moment(this.historyStore.historySelectedDate).isSame(
        new Date(),
        'day'
      )
    },
    dataTime() {
      return this.dateHelpers.moment(this.historyStore.tradingStatisticsLastUpdate as Date).format(
        'HH:mm'
      )
    },
    isMeterless() {
      return this.historyStore.isMeterless
    },
    flexPlan() {
      return this.historyStore.isFlexPlan
    },
    fixPlan() {
      return this.plans.isFixPlan()
    },
    fixFloatPlan() {
      const fixFloatArticleIds = [3, 1690];

      return this.fixPlan && fixFloatArticleIds.includes(this.plans.getGenerationPlan().articleId)
    },
    floatPlan() {
      return this.plans.isFloatPlan()
    },
    tariffName() {
      return this.plans.getGenerationPlanName();
    },
    showPrice() {
      return (
        (this.historyStore.isFlexPlan && (!this.cubeStore.planTo || this.cubeStore.planTo <= new Date())) ||
        !this.historyStore.isFlexPlan
      )
    },
    priceConsumption() {
      return (
        Math.floor((this.historyStore.historyData.totals.avgPrice as number) * 100) / 100
      ).toLocaleString('de-de', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    priceProduction() {
      return (
        Math.floor(
          (this.historyStore.historyData.totals.avgPriceProduction as number) * 100
        ) / 100
      ).toLocaleString('de-de', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    minEnergyAmountFormatted() {
      return NumberFormatUtil.format(MIN_ENERGY_TRACE);
    }
  },
}
</script>

<style lang="scss">
.statistics {
  margin-top: $spacer20;

  @media (min-width: $bp-ml) {
    margin-top: $spacer40;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    &--no-production {
      .statistics__overview {
        width: 45%;
        order: 2;
      }

      .statistics__producers {
        width: 45%;
      }
    }
  }

  &__message {
    box-shadow: 2px 2px 7px rgba(0, 0, 0, 0.15);
    padding: $spacer10 $spacer30 $spacer10 $spacer20;
    border-left: 4px solid $community;
    background: $white;
    margin: $spacer20 auto;
    max-width: 800px;

    @media (min-width: $bp-ml) {
      flex-basis: 100%;
    }

    &--unobtrusive {
      font-size: $font-size-sm;
      line-height: $line-height-sm;
      margin: $spacer025 auto;
      color: $grey-dark;
      text-align: center;
      // Since we're changing flex order in css we need to make sure this is
      // always shown last.
      order: 10;

      @media (min-width: $bp-ml) {
        flex-basis: 100%;
      }
    }

    p {
      margin: 0;
      font-size: 1.2rem;
      margin-top: 0.5rem;
      margin-bottom: 0.5rem;
    }
  }

  &__overview {
    flex-basis: 100%;
    margin-bottom: $spacer30;

    @media (min-width: $bp-ml) {
      margin-bottom: $spacer50;

      .statistics--no-production & {
        flex-basis: 45%;
        order: 2;
      }
    }

    &-title {
      flex-basis: 100%;
    }
  }

  &__overview-consumption,
  &__overview-production {
    @media (min-width: $bp-ml) {
      margin-bottom: $spacer50;
    }
  }

  &__overview-consumption,
  &__overview-production,
  &__consumers,
  &__producers {
    @media (min-width: $bp-ml) {
      flex-basis: 45%;
      width: 45%;
    }
  }
}
</style>
